import posthog from 'posthog-js'

let initialized = false

// Analytics cookies require explicit consent (RODO) — set by CookieConsent.vue.
export function hasAnalyticsConsent(): boolean {
  try {
    return localStorage.getItem('cookie_consent') === 'all'
  } catch {
    return false
  }
}

export function initAnalytics() {
  if (!hasAnalyticsConsent()) return

  const apiKey = (document.querySelector('meta[name="posthog-api-key"]') as HTMLMetaElement)?.content
  if (!apiKey || initialized) return

  const apiHost = (document.querySelector('meta[name="posthog-host"]') as HTMLMetaElement)?.content || 'https://eu.i.posthog.com'

  posthog.init(apiKey, {
    api_host: apiHost,
    capture_pageview: true,
    capture_pageleave: true,
    persistence: 'localStorage+cookie',
    loaded: (ph) => {
      if (import.meta.env.DEV) {
        ph.opt_out_capturing()
      }
    }
  })
  initialized = true
}

// Marketing pages start cookieless. Once a visitor accepts analytics, keep the
// existing anonymous identity so that the later signup can be linked to it.
export function enableAnalyticsPersistence() {
  if (!hasAnalyticsConsent()) return

  posthog.set_config({ persistence: 'localStorage+cookie' })
}

export function identifyUser(user: { id: number; email: string; first_name: string; last_name: string }, team?: { id: number; name: string }) {
  if (!initialized) return

  posthog.identify(String(user.id), {
    email: user.email,
    first_name: user.first_name,
    last_name: user.last_name,
    ...(team ? { team_id: team.id, team_name: team.name } : {})
  })

  if (team) {
    posthog.group('team', String(team.id), { name: team.name })
  }
}

export function trackEvent(event: string, properties?: Record<string, unknown>) {
  if (!initialized) return
  posthog.capture(event, properties)
}

export function resetAnalytics() {
  if (!initialized) return
  posthog.reset()
}

// Used when the user rejects analytics cookies after a prior opt-in.
export function disableAnalytics() {
  if (!initialized) return
  posthog.opt_out_capturing()
}

export function useAnalytics() {
  return {
    initAnalytics,
    enableAnalyticsPersistence,
    identifyUser,
    trackEvent,
    resetAnalytics,
    disableAnalytics,
    hasAnalyticsConsent
  }
}
